import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ViewChats.css";
import { FaEdit, FaTrash, FaTimes } from "react-icons/fa";
import openNotification from "components/utils/CustomNotification";

interface Comment {
  comment_uid: string;
  org_uid: string;
  comment_text: string;
  added_at?: string;
  added_by?: string;
  user_status?: string;
  edited_at?: string;
  user_image?: string;
  user_uid?: string;
}

interface ViewChatsProps {
  onClose: () => void;
  challengeUid: string;
  orgUid: string;
}

const ViewChats: React.FC<ViewChatsProps> = ({
  onClose,
  challengeUid,
  orgUid,
}) => {
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const apiHeaders = {
    "user-uid": userUid,
  };

  // Fetch user images by UIDs - Updated to handle duplicate image_uids
  const fetchImagesByUid = async (
    imageUids: string[],
  ): Promise<Record<string, string>> => {
    if (!imageUids || imageUids.length === 0) {
      return {};
    }

    try {
      // Remove duplicates from imageUids array
      const uniqueImageUids = [
        ...new Set(imageUids.filter((uid) => uid && uid.trim() !== "")),
      ];

      if (uniqueImageUids.length === 0) {
        return {};
      }

      const url = new URL(`${beAddress}/api/get-image-by-uid`);

      // Append all unique image_uids as separate parameters
      uniqueImageUids.forEach((uid) => {
        url.searchParams.append("image_uid_list", uid);
      });

      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      console.log("📤 Fetching images for UIDs:", uniqueImageUids);
      console.log("📤 API URL:", url.toString());

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        },
        credentials: "include",
      });

      const data = await res.json();
      console.log("📥 Image API Response:", data);

      if (data.status === "Success" && Array.isArray(data.data)) {
        const imageMap: Record<string, string> = {};

        data.data.forEach((item: any) => {
          if (item.default || !item.image) {
            imageMap[item.image_uid] = "";
            return;
          }

          // Detect MIME type from base64 signature
          const base64Img = item.image;
          let mimeType = "image/jpeg"; // Default

          if (
            base64Img.startsWith("R0lGODlh") ||
            base64Img.startsWith("R0lGODdh")
          ) {
            mimeType = "image/gif";
          } else if (base64Img.startsWith("/9j/")) {
            mimeType = "image/jpeg";
          } else if (base64Img.startsWith("iVBORw0KGgo")) {
            mimeType = "image/png";
          } else if (base64Img.startsWith("PHN2Zy")) {
            mimeType = "image/svg+xml";
          }

          const imageSrc = `data:${mimeType};base64,${base64Img}`;
          imageMap[item.image_uid] = imageSrc;

          console.log(`✅ Mapped image for ${item.image_uid}:`, {
            mimeType,
            preview: imageSrc.substring(0, 100) + "...",
          });
        });

        console.log("📦 Final imageMap:", imageMap);
        return imageMap;
      } else {
        console.error("❌ Image API response not successful:", data);
      }
    } catch (err) {
      console.error("❌ Failed to fetch images:", err);
    }

    return {};
  };

  // Fetch comments
  const fetchComments = async (pageNum: number = 1) => {
    if (!challengeUid || !orgUid) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${beAddress}/api/user-challenge-comment`,
        {
          headers: apiHeaders,
          params: {
            comment_uid: challengeUid,
            org_uid: orgUid,
            page: pageNum,
            page_size: 50,
          },
          withCredentials: true,
        },
      );

      console.log("📥 Comments API Response:", response.data);

      if (response.data.status === "Success") {
        const result = response.data.data?.comment_records || [];
        const commentsArray = Array.isArray(result) ? result : [];

        console.log("📝 Raw comments array:", commentsArray);

        // Extract ALL image_uids from the response (including duplicates)
        const allImageUids = commentsArray
          .map((comment: any) => comment.image_uid)
          .filter((uid: string) => uid && uid.trim() !== "");

        console.log("🖼️ All image_uids from comments:", allImageUids);

        // Fetch images for all unique image_uids
        let imageMap: Record<string, string> = {};
        if (allImageUids.length > 0) {
          imageMap = await fetchImagesByUid(allImageUids);
        }

        console.log("🗺️ Image map after fetching:", imageMap);

        // Map images to comments
        const commentsWithImages = commentsArray.map((comment: any) => {
          const imageUrl = comment.image_uid
            ? imageMap[comment.image_uid] || ""
            : "";

          console.log(`🔗 Mapping for comment by ${comment.added_by}:`, {
            image_uid: comment.image_uid,
            imageUrl: imageUrl ? imageUrl.substring(0, 50) + "..." : "empty",
          });

          return {
            comment_uid: comment.comment_uid,
            org_uid: comment.org_uid,
            comment_text: comment.comment_text,
            added_at: comment.added_at,
            added_by: comment.added_by,
            user_status: comment.user_status,
            edited_at: comment.edited_at,
            user_image: imageUrl,
            user_uid: comment.added_by, // Assuming added_by is the user_uid
          };
        });

        console.log("✨ Final comments with images:", commentsWithImages);
        setComments(commentsWithImages);
        setPage(response.data.data.page || 1);
        setTotalPages(response.data.data.total_pages || 1);
      } else {
        console.error("❌ Comments API not successful");
        setComments([]);
        setPage(1);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("❌ Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [challengeUid, orgUid]);

  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(
        `${beAddress}/api/user-challenge-comment`,
        {
          comment_uid: challengeUid,
          org_uid: orgUid,
          comment_text: newComment,
        },
        {
          headers: apiHeaders,
          withCredentials: true,
        },
      );

      if (response.status === 200 && response.data.status === "Success") {
        setNewComment("");
        fetchComments();

        openNotification({
          type: "success",
          message: response.data.message || "Comment added successfully!",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to add comment.",
        });
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      openNotification({
        type: "error",
        message: "Failed to add comment. Please try again.",
      });
    }
  };

  const updateComment = async () => {
    if (!editingComment) return;

    const payload = {
      comment_uid: editingComment.comment_uid,
      org_uid: editingComment.org_uid,
      comment_text: editingComment.comment_text,
      added_at: editingComment.added_at || new Date().toISOString(),
      edited_at: new Date().toISOString(),
    };

    try {
      const response = await axios.put(
        `${beAddress}/api/user-challenge-comment`,
        payload,
        {
          headers: apiHeaders,
          withCredentials: true,
        },
      );

      if (response.status === 200 && response.data.status === "Success") {
        setEditingComment(null);
        fetchComments();

        openNotification({
          type: "success",
          message: response.data.message || "Comment updated successfully!",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to update comment.",
        });
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      openNotification({
        type: "error",
        message: "Failed to update comment. Please try again.",
      });
    }
  };

  const deleteComment = async (comment: Comment) => {
    try {
      const payload = {
        ...comment,
        added_at: comment.added_at || new Date().toISOString(),
      };

      const response = await axios.delete(
        `${beAddress}/api/user-challenge-comment`,
        {
          headers: apiHeaders,
          withCredentials: true,
          data: payload,
        },
      );

      if (response.status === 200 && response.data.status === "Success") {
        fetchComments();

        openNotification({
          type: "success",
          message: response.data.message || "Comment deleted successfully!",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to delete comment.",
        });
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      openNotification({
        type: "error",
        message: "Failed to delete comment. Please try again.",
      });
    }
  };

  // Format date nicely
  // const formatDate = (dateStr?: string) => {
  //   if (!dateStr) return "";
  //   const d = new Date(dateStr);
  //   return d.toLocaleString();
  // };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);

    // Format date without seconds
    return d.toLocaleString([], {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  return (
    <>
      {/* Overlay */}
      <div className="chat-drawer-overlay" onClick={onClose} />

      {/* Drawer */}
      <div className="chat-drawer">
        {/* Header with close button */}
        <div className="chat-drawer-header">
          <h2>Challenge Comments</h2>
          <button className="chat-drawer-close" onClick={onClose}>
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="chat-drawer-content">
          {/* Comment Input Section - YouTube Style */}
          <div className="youtube-input-container">
            <div className="input-avatar">
              <div className="avatar-placeholder">
                {userUid?.charAt(0).toUpperCase() || "Y"}
              </div>
            </div>
            <div className="input-wrapper">
              <input
                type="text"
                placeholder={
                  editingComment ? "Edit your comment..." : "Add a comment..."
                }
                value={
                  editingComment ? editingComment.comment_text : newComment
                }
                onChange={(e) =>
                  editingComment
                    ? setEditingComment({
                        ...editingComment,
                        comment_text: e.target.value,
                      })
                    : setNewComment(e.target.value)
                }
                className="youtube-input-field"
              />
              <div className="input-actions">
                {editingComment && (
                  <button
                    onClick={() => {
                      setEditingComment(null);
                      setNewComment("");
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={editingComment ? updateComment : addComment}
                  className={`submit-btn ${
                    (!newComment.trim() && !editingComment) ||
                    (editingComment && !editingComment.comment_text.trim())
                      ? "disabled"
                      : ""
                  }`}
                  // disabled={
                  //   (!newComment.trim() && !editingComment) ||
                  //   (editingComment && !editingComment.comment_text.trim())
                  // }
                >
                  {editingComment ? "Save" : "Comment"}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <p className="loading-text">Loading comments...</p>
          ) : comments.length === 0 ? (
            <div className="no-comments">
              <p>No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            <ul className="chat-list">
              {comments.map((comment) => (
                <li
                  key={`${comment.comment_uid}-${comment.added_at}`}
                  className="youtube-comment-item"
                >
                  {/* User Avatar */}
                  <div className="comment-avatar">
                    {comment.user_image && comment.user_image.trim() !== "" ? (
                      <img
                        src={comment.user_image}
                        alt={comment.added_by || "User"}
                        className="user-avatar-img"
                        onError={(e) => {
                          console.error(
                            "❌ Image load failed for:",
                            comment.added_by,
                            "Image URL:",
                            comment.user_image,
                          );
                          const img = e.currentTarget as HTMLImageElement;
                          const placeholder = document.createElement("div");
                          placeholder.className = "avatar-placeholder";
                          placeholder.style.cssText = `
                            width: 40px;
                            height: 40px;
                            border-radius: 50%;
                            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: white;
                            font-weight: 600;
                            font-size: 1rem;
                          `;
                          placeholder.textContent =
                            comment.added_by?.charAt(0).toUpperCase() || "U";
                          img.parentElement?.replaceChild(placeholder, img);
                        }}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {comment.added_by?.charAt(0).toUpperCase() || "U"}
                      </div>
                    )}
                  </div>

                  {/* Comment Content */}
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">
                        {comment.added_by || "Anonymous"}
                      </span>
                      <span className="comment-date">
                        {formatDate(comment.added_at)}
                      </span>
                      {comment.edited_at && comment.edited_at.trim() !== "" && (
                        <span className="edited-indicator">(Edited)</span>
                      )}
                      {comment.user_status && (
                        <span
                          className={`user-status-badge ${comment.user_status}`}
                        >
                          {comment.user_status}
                        </span>
                      )}
                    </div>

                    <div className="comment-text">{comment.comment_text}</div>

                    {/* Action Buttons - Only show for current user's comments */}
                    {comment.added_by === userUid && (
                      <div className="comment-actions">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => setEditingComment(comment)}
                          title="Edit comment"
                        >
                          <FaEdit size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => deleteComment(comment)}
                          title="Delete comment"
                        >
                          <FaTrash size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewChats;
